'use client';

import { useMemo } from 'react';

import Link from 'next/link';

import { ChevronsUpDown, LogOut } from 'lucide-react';

import { JWTUserData } from '@kit/supabase/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@kit/ui/dropdown-menu';
import { If } from '@kit/ui/if';
import { SubMenuModeToggle } from '@kit/ui/mode-toggle';
import { ProfileAvatar } from '@kit/ui/profile-avatar';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { usePersonalAccountData } from '../hooks/use-personal-account-data';

export function PersonalAccountDropdown({
  className,
  user,
  signOutRequested,
  showProfileName = true,
  avatarClassName,
  paths,
  features,
  account,
  homeLabelKey = 'common:routes.home',
}: {
  user: JWTUserData;

  account?: {
    id: string | null;
    name: string | null;
    picture_url: string | null;
  };

  signOutRequested: () => unknown;

  paths: {
    home: string;
    /**
     * Personal account settings. Optional because the customer-facing tenant
     * headers mount this same dropdown, and `/home/*` is admin-only there —
     * the item is rendered only when a caller supplies a destination.
     */
    personalAccount?: string;
  };

  features: {
    enableThemeToggle: boolean;
  };

  /**
   * i18n key for the `paths.home` item. Defaults to a plain "Home", which is
   * what the customer-facing tenant headers want. The admin shell overrides it
   * because there `/home` is the workspace picker ("Select your workspace"),
   * not a home page — "Home" read as "the dashboard I'm already on".
   */
  homeLabelKey?: string;

  showProfileName?: boolean;
  /** Overrides the avatar's size/shape for placements outside the sidebar. */
  avatarClassName?: string;

  className?: string;
}) {
  const { data: personalAccountData } = usePersonalAccountData(
    user.id,
    account,
  );

  const signedInAsLabel = useMemo(() => {
    const email = user?.email ?? undefined;
    const phone = user?.phone ?? undefined;

    return email ?? phone;
  }, [user]);

  const displayName =
    personalAccountData?.name ?? account?.name ?? user?.email ?? '';

  const isSuperAdmin = useMemo(() => {
    const hasAdminRole = user?.app_metadata.role === 'super-admin';
    const isAal2 = user?.aal === 'aal2';

    return hasAdminRole && isAal2;
  }, [user]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Open your profile menu"
        data-test={'account-dropdown-trigger'}
        className={cn(
          'group/trigger fade-in focus:outline-primary flex cursor-pointer items-center group-data-[minimized=true]/sidebar:px-0',
          className ?? '',
          {
            // Solid hairline, not dashed. A dashed border conventionally means
            // placeholder / drop target / empty state — around a signed-in
            // account it reads as "something is missing here". The affordance
            // this control needs is a hover fill, which it already has.
            ['active:bg-secondary/50 items-center gap-3 rounded-md' +
            ' hover:bg-secondary border-border/60 border p-2 transition-colors']:
              showProfileName,
          },
        )}
      >
        <ProfileAvatar
          className={cn(
            'group-hover/trigger:border-background/50 rounded-md border border-transparent transition-colors',
            avatarClassName,
          )}
          // `rounded-[inherit]`, not a fixed `rounded-md`: this same avatar is
          // a circle in the top bar (avatarClassName → rounded-full) and a
          // rounded square in the sidebar. A hardcoded rounded-md fallback put
          // a SQUARE bordered tile inside the circular clip — its corners inset
          // from the ring and its top/bottom clipped, the "edges don't reach"
          // artifact. Inheriting the Avatar root's radius makes the initials
          // tile follow whatever shape the avatar is.
          fallbackClassName={'rounded-[inherit] border'}
          displayName={displayName ?? user?.email ?? ''}
          pictureUrl={personalAccountData?.picture_url}
        />

        <If condition={showProfileName}>
          <div
            className={
              'fade-in flex w-full flex-col truncate text-left group-data-[minimized=true]/sidebar:hidden'
            }
          >
            <span
              data-test={'account-dropdown-display-name'}
              className={'truncate text-sm'}
            >
              {displayName}
            </span>

            <span
              data-test={'account-dropdown-email'}
              className={'text-muted-foreground truncate text-xs'}
            >
              {signedInAsLabel}
            </span>
          </div>

          <ChevronsUpDown
            className={
              'text-muted-foreground mr-1 h-8 group-data-[minimized=true]/sidebar:hidden'
            }
          />
        </If>
      </DropdownMenuTrigger>

      {/* Anchored to the trigger's trailing edge, not centred on it: in the top
          bar the avatar is the last control in the row, so a centred menu
          overflowed to the window edge and sat flush against it. `align="end"`
          lines the panel's edge up with the avatar's, which inherits the bar's
          own inset; collisionPadding is the floor for every other placement. */}
      <DropdownMenuContent
        align={'end'}
        collisionPadding={12}
        className={'!z-[9999] !bg-white xl:min-w-[15rem]! dark:!bg-neutral-950'}
      >
        <DropdownMenuItem className={'h-10! rounded-none'}>
          <div
            className={'flex flex-col justify-start truncate text-left text-xs'}
          >
            <div className={'text-muted-foreground'}>
              <Trans i18nKey={'common:signedInAs'} />
            </div>

            <div>
              <span className={'block truncate'}>{signedInAsLabel}</span>
            </div>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Moved here out of the workspace switcher: that list is the teams you
            can work in, and your own account is not one of them. It leads the
            group because this is the profile menu — "you" first, then where you
            work. */}
        <If condition={paths.personalAccount}>
          {(personalAccountPath) => (
            <DropdownMenuItem asChild>
              <Link
                data-test={'account-dropdown-personal-account'}
                className={'s-full flex cursor-pointer items-center'}
                href={personalAccountPath}
              >
                <Trans i18nKey={'common:profile'} />
              </Link>
            </DropdownMenuItem>
          )}
        </If>

        <DropdownMenuItem asChild>
          <Link
            className={'s-full flex cursor-pointer items-center'}
            href={paths.home}
          >
            <Trans i18nKey={homeLabelKey} />
          </Link>
        </DropdownMenuItem>

        <If condition={isSuperAdmin}>
          <DropdownMenuSeparator />

          <DropdownMenuItem asChild>
            <Link
              className={
                's-full flex cursor-pointer items-center text-yellow-700 dark:text-yellow-500'
              }
              href={'/admin'}
            >
              Super Admin
            </Link>
          </DropdownMenuItem>
        </If>

        {/* Each optional block carries its OWN leading separator. Keeping a
            standalone one here instead printed two hairlines in a row whenever
            the theme toggle was disabled. */}
        <If condition={features.enableThemeToggle}>
          <DropdownMenuSeparator />

          <SubMenuModeToggle />
        </If>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          data-test={'account-dropdown-sign-out'}
          role={'button'}
          className={'cursor-pointer'}
          onClick={signOutRequested}
        >
          {/* Sign out is the one item that keeps its icon: it's the menu's
              only destructive/terminal action, so it's worth setting apart from
              the plain navigation rows above it. gap-2 matches the theme
              submenu's icon spacing so the two stay optically aligned. */}
          <span className={'flex w-full items-center gap-2'}>
            <LogOut className={'h-4 w-4'} />

            <Trans i18nKey={'auth:signOut'} />
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
